import M360MarkdownLink from "@/lib/M360MarkdownLink";
import Avatar from "@/components/Avatar/Avatar";
import Icon from "@/components/Icon";
import { m360Paths, normalizeM360ImageSrc } from "@/lib/m360Paths";
import type { M360StructuredBlock as StructuredBlock } from "./m360StructuredContent";
import { normalizeChatDisplayText } from "./normalizeChatDisplayText";
import { AgentLinkChip } from "./agentChat/cards/shared";
import { RecordPreviewRow } from "./agentChat/cards/RecordPreviewRow";

type M360StructuredBlockProps = {
  block: StructuredBlock;
};

const personNameLinkClassName =
  "text-title-medium font-medium text-sys-brand-on-surface no-underline decoration-transparent hover:text-sys-brand-primary";

function PersonCard({ block }: { block: Extract<StructuredBlock, { type: "person" }> }) {
  const displayName = normalizeChatDisplayText(block.name);
  const subtitle = block.subtitle ? normalizeChatDisplayText(block.subtitle) : undefined;
  const defaultLinks = [
    { label: "Overview", href: m360Paths.personOverview(block.pgaId) },
    { label: "Interactions", href: m360Paths.personInteractions(block.pgaId) },
    { label: "Notes", href: m360Paths.personNotes(block.pgaId) }
  ];
  const links = [...defaultLinks];
  for (const link of block.links ?? []) {
    if (!links.some((existing) => existing.href === link.href)) {
      links.push(link);
    }
  }

  return (
    <article className="overflow-hidden rounded-2xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest shadow-web-2">
      <div className="flex items-start gap-4 bg-gradient-to-br from-sys-brand-primary-container/30 via-sys-brand-surface-container-low to-sys-brand-surface-container-lowest p-4">
        <Avatar
          picture={normalizeM360ImageSrc(block.imageUrl) ?? undefined}
          size="xlarge"
          className="shrink-0 ring-2 ring-sys-brand-surface-container-lowest"
          iconClassName="text-sys-brand-on-surface-variant"
        />
        <div className="min-w-0 flex-1 space-y-1.5 pt-1">
          <p className="text-label-medium font-medium text-sys-brand-primary">Member profile</p>
          <h3 className="text-title-large text-sys-brand-on-surface">
            <M360MarkdownLink href={m360Paths.personOverview(block.pgaId)} className={personNameLinkClassName}>
              {displayName}
            </M360MarkdownLink>
          </h3>
          <p className="font-mono text-body-small text-sys-brand-on-surface-variant">PGA ID {block.pgaId}</p>
          {subtitle ? (
            <p className="text-body-small leading-relaxed text-sys-brand-on-surface-variant">{subtitle}</p>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 border-t border-sys-brand-outline-variant/70 bg-sys-brand-surface-container-lowest px-4 py-3">
        {links.map((link) => (
          <AgentLinkChip key={`${link.href}-${link.label}`} {...link} />
        ))}
      </div>
    </article>
  );
}

function InteractionCard({ block }: { block: Extract<StructuredBlock, { type: "interaction" }> }) {
  const href = m360Paths.personInteraction(block.pgaId, block.interactionId);
  const title = normalizeChatDisplayText(block.title);

  return (
    <article className="rounded-xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest p-4 shadow-web-1">
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sys-brand-secondary-container text-sys-brand-on-secondary-container"
          aria-hidden
        >
          <Icon name="forum" size={20} />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="text-title-small text-sys-brand-on-surface">
            <M360MarkdownLink href={href}>{title}</M360MarkdownLink>
          </h3>
          {block.subtitle ? (
            <p className="text-body-small text-sys-brand-on-surface-variant">
              {normalizeChatDisplayText(block.subtitle)}
            </p>
          ) : null}
          {block.meta ? (
            <p className="text-body-small text-sys-brand-on-surface">{normalizeChatDisplayText(block.meta)}</p>
          ) : null}
        </div>
      </div>
      <div className="mt-3">
        <AgentLinkChip label="Open interaction" href={href} />
      </div>
    </article>
  );
}

function LinksPanel({ block }: { block: Extract<StructuredBlock, { type: "links" }> }) {
  return (
    <section className="rounded-xl border border-dashed border-sys-brand-outline-variant bg-sys-brand-surface-container-low/60 p-3">
      {block.title ? (
        <h3 className="mb-2 text-label-large text-sys-brand-on-surface-variant">
          {normalizeChatDisplayText(block.title)}
        </h3>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {block.links.map((link) => (
          <AgentLinkChip key={`${link.href}-${link.label}`} {...link} />
        ))}
      </div>
    </section>
  );
}

function RecordListPanel({ block }: { block: Extract<StructuredBlock, { type: "record_list" }> }) {
  return (
    <section className="space-y-2">
      {block.title ? (
        <h3 className="text-label-large text-sys-brand-on-surface">{normalizeChatDisplayText(block.title)}</h3>
      ) : null}
      <ul className="space-y-2">
        {block.items.map((item, index) => (
          <RecordPreviewRow key={`${item.title}-${index}`} item={item} />
        ))}
      </ul>
    </section>
  );
}

export default function M360StructuredBlockView({ block }: M360StructuredBlockProps) {
  switch (block.type) {
    case "person":
      return <PersonCard block={block} />;
    case "interaction":
      return <InteractionCard block={block} />;
    case "links":
      return <LinksPanel block={block} />;
    case "record_list":
      return <RecordListPanel block={block} />;
    default:
      return null;
  }
}
